<script setup>
import SidebarCard from '@/components/cards/SidebarCard.vue';
import AppFooter from '@/components/AppFooter.vue';
import SIdeBarPostList from '@/components/cards/SIdeBarPostList.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import AppHeader from '@/components/headers/AppHeader.vue';
import ImageCard from '@/components/cards/ImageCard.vue';
import PostDetailComponent from '@/components/PostDetailComponent.vue';
import PostHeader from '@/components/headers/PostHeader.vue';
import PostAuthorComponent from '@/components/PostAuthorComponent.vue';
import {computed, onMounted, watch} from 'vue';
import TopContributorComponent from '@/components/cards/TopContributorComponent.vue';
import {
  ArrowRightIcon,
  ArrowUpRightIcon, BuildingOfficeIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapIcon, MapPinIcon,
  PhoneIcon
} from '@heroicons/vue/16/solid';
import ReviewForm from '@/components/form/ReviewForm.vue';
import ReviewsCard from '@/components/cards/ReviewsCard.vue';
import OverflowImageSlider from '@/components/OverflowImageSlider.vue';
import { useBusinessStore } from '@/stores/business';
import { useMiscStore } from '@/stores/misc';
import { useRoute } from 'vue-router';
import { useContributorStore } from '@/stores/contributor';
import SidebarContributorPostList from '@/components/cards/SidebarContributorPostList.vue';
import OverlayComponent from "@/components/OverlayComponent.vue";
import { delay } from '@/utils/delay';
import RatingCardComponent from "@/components/cards/RatingCardComponent.vue";
import TagComponent from "@/components/TagComponent.vue";

const store = useBusinessStore(),
  misc = useMiscStore(),
  contributorStore = useContributorStore(),
  contributor = computed(() => contributorStore.contributor),
  route = useRoute(),
  currentSector = computed(() => misc.currentSector),
  currentCategory = computed(() => misc.currentCategory);

// Get sector ID from currentSector store
const getSectorId = () => currentSector.value?.id;

const getCategoryId = () => currentCategory.value?.id;

// Initial data fetch
const fetchData = async () => {
  try {
    const { sector, category, business, username } = route.params;
    
    // First ensure we have the category data if we have a category slug
    if (category && !currentCategory.value?.id) {
      await misc.fetchCategory(category);
      // Wait for the store to update
      await delay();
    }

    if (!currentSector.value?.id) {
      await misc.updateCurrentSector(sector);
      await delay();
    }
    
    // Get IDs - they should now be available from the store
    const sectorId = getSectorId();
    const categoryId = getCategoryId();
    
    // Only proceed if we have all required parameters
    if (!sectorId || !categoryId || !business) {
      console.error('Missing required parameters:', { sectorId, categoryId, business, currentSector, currentCategory });
      return;
    }
    
    // Fetch the business data with the resolved IDs
    await store.fetchBusiness(sectorId, categoryId, business);

    // Fetch similar businesses
    if (username) {
      await contributorStore.fetchContributor(username);
      if (contributor.value?.id) {
        await contributorStore.fetchSimilarBusinesses(contributor.value.id, business);
      }
    } else {
      await store.fetchSimilarBusinesses(business);
    }
    
    // Fetch popular contributors
    await contributorStore.fetchPopular('contributions');
  } catch (error) {
    console.error('Error in fetchData:', {
      error: error.message,
      routeParams: route.params,
      currentSector: currentSector.value,
      currentCategory: currentCategory.value
    });
  }
};

// Initial fetch
onMounted(fetchData);

// Watch for route changes
watch(
  () => route.params,
  (newParams, oldParams) => {
    if (JSON.stringify(newParams) !== JSON.stringify(oldParams)) {
      fetchData();
    }
  },
  // { immediate: true, deep: true }
);
const business = computed(() => store.business),
  similarBusinesses = computed(
      () => route.params.username !== undefined 
      ? contributorStore.similarBusinesses 
      : store.similarBusinesses
  ),
  contributorTopRated = computed(() => contributorStore.topRatedBusinesses),
loading = computed(() => misc.loading),
  loadingSimilarBusinesses = computed(
      () => route.params.username !== undefined 
          ? contributorStore.loadingSimilarBusinesses 
          : store.loadingSimilarBusinesses
  ),
  loadingTopRatedBusinesses = computed(() => contributorStore.loadingTopRatedBusinesses),
  topContributors = computed(() => contributorStore.popular || contributorStore.topRated),
  loadingTopContributors = computed(() => contributorStore.loadingTopContributors);

watch(
  () => [business.value, currentCategory.value],
  ([business, category]) => {
    if (business?.id && category?.id) {
      contributorStore.fetchTopRatedBusinesses(business.creator.id, null, category.id);
    }
  }
)

</script>

<template>
  <DefaultLayout :loading="loading">
    <template #header>
      <AppHeader
        v-if="business"
        :bg-image="business.baseImageUrl"
        :is-post-header="true"
      >
        <template #content>
          <PostHeader
            :title="business.name"
            :description="business.shortDescription"
            :subtitle="business.category?.name"
          />
        </template>
      </AppHeader>
    </template>
    <template
      v-if="business"
      #main
    >
      <div class="relative">
        <ImageCard
          v-if="business?.baseImageUrl"
          :image="{ url: business?.baseImageUrl, alt: business?.name}"
          class="md:hidden"
        />
        <OverlayComponent class="rounded-lg" />
      </div>
      <PostDetailComponent :business="business" />

      <SidebarCard
        class="md:hidden"
        :as-content="business?.workingHours?.length > 0"
      >
        <template #header-title>
          Working hours
        </template>
        <template #body>
          <div id="working-hours">
            <div class="text-gray-600 grid grid-cols-1 lg:grid-cols-2 gap-x-2 xl:gap-x-16 gap-y-2 p-4">
              <div
                v-for="item in business?.workingHours"
                :key="item.day"
                class="text-left"
              >
                <p class="text-xs flex flex-wrap justify-between">
                  <span class="font-bold block mr-2 text-gray-400">
                    {{ item.day }}:
                  </span>
                  <span class="flex">
                    <span>{{ item.from }}</span>
                    -
                    <span>{{ item.to }}</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </template>
      </SidebarCard>

      <div v-if="business?.images?.length > 0">
        <overflow-image-slider :images="business.images" />
      </div>
      <PostAuthorComponent
        :creator="business.creator"
      />
      <hr class="my-5">

      <review-form />

      <reviews-card :reviews="business.reviews" />
    </template>
    <template #aside>
      <div class="relative">
        <ImageCard
          v-if="business?.baseImageUrl"
          :image="{ url: business?.baseImageUrl, alt: business?.name}"
        />
        <OverlayComponent class="rounded-lg" />
      </div>
      <RatingCardComponent
        :is-side-by-side="true"
        :rating="business?.rating"
        :ratings="business?.ratings"
        :class="`bg-white rounded-lg mb-4`"
      />
      <sidebar-card
        :as-content="business"
        class="hidden md:block"
      >
        <template #header-title>
          Contact
        </template>
        <template #body>
          <div
            id="info"
            class="bg-white mt-4 rounded-xl ay-shadow shadow col-span-5 order-2 lg:order-3 border-gray-100 p-6 mb-4"
          >
            <div>
              <div class="grid grid-cols-1 md:grid-cols-4">
                <div class="col-span-2 mb-4 md:mb-1 text-left">
                  <span class="text-gray-300 mb-3 text-xs items-center md:text-sm flex font-semibold">
                    <BuildingOfficeIcon
                      id="bookmark"
                      data-toggle="tooltip"
                      class="h-5 w-5 text-center text-gray-300 cursor-pointer mr-1"
                      aria-hidden="true"
                      style="cursor:pointer;"
                    />
                    Category
                  </span>
                  <TagComponent
                    :title="business?.category?.name"
                    :class="`text-xs text-left md:mt-4 rounded-md px-1.5 py-0.5`"
                    :bg-color="business?.category?.background"
                    :color="business?.category?.color"
                  />
                </div>
                
                <div class="col-span-2 mb-4 md:mb-8 text-left">
                  <span class="text-gray-300 text-xs md:text-sm flex mb-3 font-semibold">
                    <MapIcon
                      id="bookmark"
                      data-toggle="tooltip"
                      class="h-5 w-5 text-center text-gray-300 cursor-pointer mr-1"
                      aria-hidden="true"
                      style="cursor:pointer;"
                    />
                    Location
                  </span>
                  <span>
                    <!--                  <a href="http://gh-top.test/chilling/restaurant-bar" class="text-muted">-->
                    <span class="text-gray-500 font-medium text-sm">
                      {{ business?.region?.name }}
                    </span>
                    <!--                  </a>-->
                  </span>
                </div>

                <div class="col-span-2 mb-4 md:mb-8">
                  <span class="text-gray-300 text-xs items-center md:text-sm flex font-semibold">
                    <PhoneIcon
                      id="bookmark"
                      data-toggle="tooltip"
                      class="h-5 w-5 text-center text-gray-300 cursor-pointer mr-1"
                      aria-hidden="true"
                      style="cursor:pointer;"
                    />
                    Phone
                  </span>
                  <span class="block text-left md:mt-3">
                    <ul>
                      <li
                        v-for="(phone, index) in business?.phone"
                        :key="index"
                      >
                        <a
                          :href="`tel:${ phone }`"
                          class="text-gray-500 text-sm font-medium hover:text-red-500 dark:text-red-400 dark:hover:text-red-500"
                        >
                          <span>{{ phone }}</span>
                        </a>
                      </li>
                    </ul>
                  </span>
                </div>

                <div
                  class="col-span-2 text-left"
                >
                  <span
                    class="text-gray-300 text-xs items-center md:text-sm flex font-semibold"
                  >
                    <MapPinIcon
                      id="bookmark"
                      data-toggle="tooltip"
                      class="h-5 w-5 text-gray-300 cursor-pointer mr-1"
                      aria-hidden="true"
                      style="cursor:pointer;"
                    />
                    Address
                  </span>
                  <span class="md:mt-3 block break-all">
                    <!--                  <a href="http://gh-top.test/chilling/restaurant-bar" class="text-muted">-->
                    <span class="text-gray-500 font-medium text-sm">
                      {{ business?.city?.name }} - {{ business?.address }}
                    </span>
                    <!--                  </a>-->
                  </span>
                </div>

                <div class="col-span-2 text-left">
                  <span class="text-gray-300 text-xs items-center md:text-sm flex font-semibold">
                    <GlobeAltIcon
                      id="bookmark"
                      data-toggle="tooltip"
                      class="h-5 w-5 text-center text-gray-300 cursor-pointer mr-1"
                      aria-hidden="true"
                      style="cursor:pointer;"
                    />
                    Website
                  </span>
                  <span
                    class="md:mt-4 inline-flex"
                    data-toggle="tooltip"
                    π
                    title="Go to website"
                  >
                    <a
                      :href="`${ business?.url }`"
                      class="text-sm hover:text-green-600 transition-all duration-700 text-gray-500 font-medium"
                      target="_blank"
                    >
                      <span class="flex items-center">
                        {{ 'Visite' }}
                        <ArrowUpRightIcon
                          class="h-5 w-5 text-green-700"
                        />
                      </span>
                    </a>
                  </span>
                </div>

                <div class="col-span-2 text-left">
                  <span class="text-gray-300 text-xs items-center md:text-sm flex font-semibold">
                    <EnvelopeIcon
                      id="bookmark"
                      data-toggle="tooltip"
                      class="h-5 w-5 text-gray-300 cursor-pointer mr-1"
                      aria-hidden="true"
                      style="cursor:pointer;"
                    />
                    E-mail
                  </span>
                  <span class="md:mt-2 block break-all">
                    <a
                      :href="`mailto:${ business?.email }`"
                      class="text-gray-500 text-sm font-medium"
                    >
                      <span class="">{{ business?.email }}</span>
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </sidebar-card>
      <SidebarCard
        class="hidden"
        :as-content="business?.workingHours?.length > 0"
      >
        <template #header-title>
          Working hours
        </template>
        <template #body>
          <div id="working-hours">
            <div class="text-gray-600 grid grid-cols-1 lg:grid-cols-2 gap-x-2 xl:gap-x-16 gap-y-2 p-4">
              <div
                v-for="item in business?.workingHours"
                :key="item.day"
                class="text-left"
              >
                <p class="text-xs flex flex-wrap justify-between">
                  <span class="font-bold block mr-2 text-gray-400">
                    {{ item.day }}:
                  </span>
                  <span class="flex">
                    <span>{{ item.from }}</span>
                    -
                    <span>{{ item.to }}</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </template>
      </SidebarCard>
      <SidebarCard
        :as-content="contributorTopRated.length > 0"
        :loading="loadingTopRatedBusinesses"
      >
        <template #header-title>
          <span class="text-yellow-100 block">{{ business?.creator?.name }}</span>
          <span class="font-medium text-xs">Top rated businesses</span>
        </template>
        <template #body>
          <SidebarContributorPostList
            v-if="contributor?.id"
            :businesses="contributorTopRated"
            type="top-rated"
          />
          <SIdeBarPostList
            v-else
            :businesses="contributorTopRated"
            type="top-rated"
          />
        </template>
        <template #footer>
          <div
            class="
            text-right 
            font-medium 
            text-gray-500 
            hover:text-green-500
            transition-all duration-500"
          >
            <router-link
              v-if="contributorTopRated?.length > 0"
              :to="`/users/${business?.creator?.id}/businesses`"
              class="text-xs font-semibold uppercase"
            >
              View all posts
              <ArrowRightIcon class="inline h-4 w-4 ml-1" />
            </router-link>
          </div>
        </template>
      </SidebarCard>
      <SidebarCard
        v-if="similarBusinesses.length > 0"
        :as-content="similarBusinesses.length > 0"
        :loading="loadingSimilarBusinesses"
      >
        <template #header-title>
          <span class="font-medium text-xs">Similar businesses for</span>
          <span class="text-yellow-100 block">{{ currentCategory?.name }}</span>
        </template>
        <template #body>
          <SidebarContributorPostList
            v-if="contributor?.id"
            :businesses="similarBusinesses"
            type="similar"
          />
          <SIdeBarPostList
            v-else
            :businesses="similarBusinesses"
            type="similar"
          />
        </template>
      </SidebarCard>
      <SidebarCard
        :as-content="topContributors?.items?.length > 0"
        :loading="loadingTopContributors"
      >
        <template #header-title>
          <span class="font-medium text-xs">
            Top contributors in
          </span>
          <span class="text-yellow-100 block">
            {{ currentCategory?.name }}
          </span>
        </template>
        <template #body>
          <TopContributorComponent :contributors="topContributors" />
        </template>
        <template #footer>
          <div
            class="
            text-right
            text-red-600
            hover:text-red-500
            transition-all duration-500"
          >
            <router-link
              :to="`/contributors`"
              class="text-xs font-semibold uppercase"
            >
              View all contributors
              <ArrowRightIcon class="inline h-4 w-4 ml-1" />
            </router-link>
          </div>
        </template>
      </SidebarCard>
    </template>
  </DefaultLayout>
  <AppFooter />
</template>

<style scoped></style>
